/** Ловушка для падений интерфейса: белый экран — худший из ответов.
 *
 * Ленивый чанк игры после свежего выката отвечает 404, вебвью без WebGL не
 * поднимает сцену, модель не доехала — без ловушки React размонтирует
 * корень целиком, и человек остаётся с пустым экраном без каталога и
 * корзины. Здесь падение остаётся в своей рамке, а кнопка предлагает
 * единственное, что помогает: перезагрузить.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Что сказать вместо экрана — коротко и по делу */
  title?: string;
}

interface State {
  failed: boolean;
  detail: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, detail: '' };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, detail: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // В консоль вебвью, чтобы при разборе было за что зацепиться
    console.error('Витрина: экран упал', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="glass mx-auto my-6 max-w-[360px] rounded-card px-4 py-5 text-center">
        <div className="text-[15px] font-[680]">{this.props.title ?? 'Что-то сломалось'}</div>
        <div className="mt-1 text-[12px] text-hint">
          Скорее всего, витрина обновилась, пока была открыта. Перезагрузка обычно помогает.
        </div>
        <button
          type="button"
          onClick={() => location.reload()}
          className="mt-3 rounded-xl bg-mint px-4 py-2.5 text-[14px] font-[650] text-mint-ink active:opacity-85"
        >
          Перезагрузить
        </button>
        {this.state.detail && (
          <div className="mt-3 break-words text-[11px] text-hint/70">{this.state.detail}</div>
        )}
      </div>
    );
  }
}
